import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Wallet, Search, ChevronLeft, ChevronRight, RefreshCw,
  ArrowUpCircle, ArrowDownCircle, FileText, Filter
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function WalletLedger() {
  const { user } = useAuth();
  const token = localStorage.getItem("sendtoo_token");
  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [ledger, setLedger] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [txnType, setTxnType] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("transactions");

  useEffect(() => {
    fetch(`${API}/api/users?role=customer`, { headers }).then(r => r.json()).then(setCustomers);
  }, []);

  const fetchLedger = useCallback(async () => {
    if (!selectedCustomer) return;
    setLoading(true);
    const params = new URLSearchParams({ page: page.toString(), page_size: "20" });
    if (search) params.set("search", search);
    if (txnType) params.set("txn_type", txnType);
    const r = await fetch(`${API}/api/v2/wallet-ledger/${selectedCustomer}?${params}`, { headers });
    if (r.ok) setLedger(await r.json());
    setLoading(false);
  }, [selectedCustomer, page, search, txnType]);

  useEffect(() => { fetchLedger(); }, [fetchLedger]);

  return (
    <DashboardLayout title="Admin Panel" user={user} portalColor="red">
      <div className="p-4 space-y-4" data-testid="wallet-ledger-page">
        <h1 className="text-2xl font-bold text-gray-900">Wallet Ledger</h1>
        <p className="text-sm text-gray-500">Delhivery-style transaction ledger with freight charges, weight adjustments, and reconciliation credits/debits.</p>

        {/* Customer Selector */}
        <div className="flex items-center gap-3">
          <select className="border rounded px-3 py-2 text-sm min-w-[250px]" value={selectedCustomer}
            onChange={e => { setSelectedCustomer(e.target.value); setPage(1); }}
            data-testid="ledger-customer-select">
            <option value="">Select Customer</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
          </select>
        </div>

        {ledger && selectedCustomer && (
          <>
            {/* Summary Bar (like Delhivery) */}
            <div className="bg-white border rounded-lg p-4 flex items-center gap-8" data-testid="ledger-summary">
              <div>
                <div className="text-xs text-gray-500 flex items-center gap-1"><Wallet className="w-3 h-3" /> Current Balance</div>
                <div className={`text-2xl font-bold ${ledger.current_balance >= 0 ? "text-green-600" : "text-red-600"}`}>
                  ₹ {ledger.current_balance?.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 flex items-center gap-1"><ArrowUpCircle className="w-3 h-3" /> Total Credit</div>
                <div className="text-xl font-bold text-green-600">₹{ledger.total_credit?.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 flex items-center gap-1"><ArrowDownCircle className="w-3 h-3" /> Total Debit</div>
                <div className="text-xl font-bold text-red-600">₹{ledger.total_debit?.toLocaleString()}</div>
              </div>
              <div className="text-sm text-gray-600">
                <div className="font-medium">{ledger.customer_name}</div>
                <div className="text-xs text-gray-400">{ledger.customer_email}</div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-4 border-b">
              <button onClick={() => setTab("transactions")} className={`pb-2 text-sm font-medium border-b-2 ${tab === "transactions" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500"}`}>
                Transactions
              </button>
              <button onClick={() => setTab("recharges")} className={`pb-2 text-sm font-medium border-b-2 ${tab === "recharges" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500"}`}>
                Recharges
              </button>
            </div>

            {/* Filters (Delhivery-style) */}
            <div className="flex flex-wrap items-center gap-3 bg-gray-50 rounded-lg p-3" data-testid="ledger-filters">
              <div className="flex items-center gap-2 border rounded bg-white px-2 py-1">
                <span className="text-xs text-gray-500">AWB</span>
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by AWB number"
                  className="border-0 h-7 text-sm w-40 focus-visible:ring-0" data-testid="ledger-awb-search" />
              </div>
              <select className="border rounded px-3 py-1.5 text-sm bg-white" value={txnType}
                onChange={e => { setTxnType(e.target.value); setPage(1); }} data-testid="ledger-txn-type">
                <option value="">Transaction Type</option>
                <option value="FREIGHT_CHARGE">Freight Charge</option>
                <option value="WEIGHT_ADJUSTMENT_DEBIT">Weight Adjustment (Debit)</option>
                <option value="RECONCILIATION_CREDIT">Reconciliation Credit</option>
                <option value="SHIPMENT_MANIFESTED">Shipment Manifested</option>
                <option value="SHIPMENT_NOT_PICKED">Shipment Not Picked</option>
                <option value="RECHARGE">Recharge</option>
              </select>
              <Button size="sm" onClick={() => { setPage(1); fetchLedger(); }}><Search className="w-3 h-3 mr-1" /> Search</Button>
            </div>

            {/* Transactions Table (Delhivery-style) */}
            <div className="bg-white border rounded-lg overflow-hidden" data-testid="ledger-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                    <tr>
                      <th className="px-3 py-3 text-left">Transaction Details</th>
                      <th className="px-3 py-3 text-left">Account Details</th>
                      <th className="px-3 py-3 text-left">Order ID</th>
                      <th className="px-3 py-3 text-left">AWB / LRN</th>
                      <th className="px-3 py-3 text-left">Weight & Zone</th>
                      <th className="px-3 py-3 text-left">Description</th>
                      <th className="px-3 py-3 text-right">Credit</th>
                      <th className="px-3 py-3 text-right">Debit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {loading ? (
                      <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400"><RefreshCw className="w-4 h-4 animate-spin inline mr-2" />Loading...</td></tr>
                    ) : (ledger.items || []).length === 0 ? (
                      <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400"><FileText className="w-5 h-5 inline mr-2" />No transactions found</td></tr>
                    ) : ledger.items.map(t => (
                      <tr key={t.id} className="hover:bg-gray-50" data-testid={`ledger-row-${t.txn_id}`}>
                        <td className="px-3 py-3">
                          <div className="text-blue-600 font-mono text-xs hover:underline cursor-pointer">{t.txn_id}</div>
                          <div className="text-xs text-gray-400">{new Date(t.created_at).toLocaleDateString()} {new Date(t.created_at).toLocaleTimeString()}</div>
                        </td>
                        <td className="px-3 py-3 text-xs text-gray-600">{t.vendor_code}</td>
                        <td className="px-3 py-3 font-mono text-xs">{t.order_id || "—"}</td>
                        <td className="px-3 py-3 font-mono text-xs">{t.awb || "—"}</td>
                        <td className="px-3 py-3 text-xs">
                          {t.weight_kg ? <div>{t.weight_kg * 1000} gm</div> : <div>···</div>}
                          {t.zone && <div className="text-purple-600">{t.zone}</div>}
                        </td>
                        <td className="px-3 py-3 text-xs text-gray-700 max-w-[200px]">{t.description}</td>
                        <td className="px-3 py-3 text-right font-medium">
                          {t.credit > 0 ? <span className="text-green-600">+₹{t.credit.toLocaleString()}</span> : <span className="text-gray-300">·</span>}
                        </td>
                        <td className="px-3 py-3 text-right font-medium">
                          {t.debit > 0 ? <span className="text-red-600">-₹{t.debit.toLocaleString()}</span> : <span className="text-gray-300">·</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {(ledger.total_pages || 0) > 1 && (
                <div className="px-4 py-3 border-t flex justify-between items-center bg-gray-50">
                  <span className="text-sm text-gray-500">{ledger.total} transactions</span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                    <span className="text-sm text-gray-600 px-2 py-1">Page {page} of {ledger.total_pages}</span>
                    <Button size="sm" variant="outline" disabled={page >= ledger.total_pages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
